import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';

/**
 * SeeAllLink — the one "see the rest of this" link (Kurtis, 2026-10-02).
 * Blue text and a chevron, 44px tall, at the right of a section heading
 * (SectionHeading's `action` slot). Replaces five styles: chevron, arrow,
 * "→" in the string, bare underlined text, and Home's 20px-tall link.
 *
 * Use it to open the full list somewhere else (a page, a sheet). A toggle
 * that expands a list in place ("Show more" / "Show less") is not this.
 *
 * Props:
 * @param {string} to - Route; renders a router <Link> (keeps cmd-click)
 * @param {Function} onClick - For a sheet or modal instead of a route
 * @param {React.ReactNode} children - Label; defaults to "See all"
 * @param {string} className - Placement
 * Anything else (aria-label, data-*) passes through.
 */
function SeeAllLink({ to = null, onClick, children, className = '', ...rest }) {
  const { t } = useTranslation('common');
  // -mr-2 lines the chevron up with the content's right edge while the tap
  // area extends past it.
  const classes = `inline-flex items-center gap-0.5 min-h-[44px] pl-2 pr-1 -mr-2 rounded-lg text-sm font-semibold text-primary hover:bg-primary-lighter transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light ${className}`;
  const content = (
    <>
      {children ?? t('controls.seeAll')}
      <Icon name="chevronRight" className="w-4 h-4 flex-shrink-0" />
    </>
  );
  if (to) return <Link to={to} className={classes} {...rest}>{content}</Link>;
  return <button type="button" onClick={onClick} className={classes} {...rest}>{content}</button>;
}

export default SeeAllLink;
